import { Link } from 'react-router-dom'
import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import {
  BROKER_REQUEST_ITEM_STATUS_LABELS_RU,
  BROKER_REQUEST_STATUS_LABELS_RU,
  CUSTOMS_STATUS_LABELS_RU,
  formatDutyRate,
  isSafeDatasheetUrl,
  type BrokerRequestDetailDto,
  type BrokerRequestItemStatus,
  type BrokerRequestStatus,
  type BrokerRequestSummaryDto,
  type CustomsCheckStatus,
} from '@crm/shared'
import { CustomsStatusBadge } from '../../../shared/ui/Badge'
import { BrokerItemStatusBadge, BrokerRequestStatusBadge } from './BrokerStatusBadge'

/**
 * Колонки таблиц раздела «Запросы брокеру»: журнал, партномера запроса и
 * позиции заявок. Отформатированные поля (даты, суммы) сортируются по сырому
 * значению — comparator читает его из строки.
 */

export function formatDateRu(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('ru-RU') : ''
}

export function formatDateTimeRu(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Сбор из ответа брокера: 0 — «0», больше нуля — повышенный. */
export function formatCustomsFee(fee: number | null): string {
  if (fee == null) return ''
  if (fee <= 0) return '0'
  return `Повышенный ${Math.round(fee).toLocaleString('ru-RU')} ₽`
}

const byTime =
  <K extends string>(key: K) =>
  (
    _a: unknown,
    _b: unknown,
    nodeA: { data?: Partial<Record<K, string | null>> },
    nodeB: { data?: Partial<Record<K, string | null>> },
  ) => {
    const ta = nodeA.data?.[key] ? new Date(nodeA.data[key] as string).getTime() : 0
    const tb = nodeB.data?.[key] ? new Date(nodeB.data[key] as string).getTime() : 0
    return ta - tb
  }

const dash = <span className="text-gray-400">—</span>

function TextCell({ value }: { value: string | null | undefined }) {
  return value ? <span title={value}>{value}</span> : dash
}

function DatasheetLink({ url }: { url: string | null }) {
  if (!isSafeDatasheetUrl(url)) return dash
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={url}
      className="text-brand-600 hover:text-brand-700 font-medium"
    >
      Открыть
    </a>
  )
}

// ─── Журнал запросов ────────────────────────────────────────────────────────

export type BrokerJournalRow = {
  id: string
  fileCode: string
  status: BrokerRequestStatus
  statusLabel: string
  itemsCount: number
  answeredCount: number
  createdAt: string
  createdLabel: string
  answeredAt: string | null
  answeredLabel: string
  logistName: string
  brokerName: string
  note: string
}

export function toJournalRow(r: BrokerRequestSummaryDto): BrokerJournalRow {
  return {
    id: r.id,
    fileCode: r.fileCode,
    status: r.status,
    statusLabel: BROKER_REQUEST_STATUS_LABELS_RU[r.status],
    itemsCount: r.itemsCount,
    answeredCount: r.answeredCount,
    createdAt: r.createdAt,
    createdLabel: formatDateRu(r.createdAt),
    answeredAt: r.answeredAt,
    answeredLabel: formatDateRu(r.answeredAt),
    logistName: r.logist?.name ?? '',
    brokerName: r.broker?.name ?? '',
    note: r.note ?? '',
  }
}

export function buildJournalColDefs(): ColDef<BrokerJournalRow>[] {
  return [
    {
      field: 'fileCode',
      headerName: 'Код файла',
      minWidth: 190,
      cellRenderer: ({ data }: ICellRendererParams<BrokerJournalRow>) =>
        data ? (
          <Link
            to={`/customs/requests/${data.id}`}
            className="font-mono text-xs text-brand-600 hover:text-brand-700 font-medium"
          >
            {data.fileCode}
          </Link>
        ) : null,
    },
    {
      field: 'statusLabel',
      headerName: 'Статус',
      minWidth: 140,
      cellRenderer: ({ data }: ICellRendererParams<BrokerJournalRow>) =>
        data ? <BrokerRequestStatusBadge status={data.status} /> : null,
    },
    {
      field: 'brokerName',
      headerName: 'Брокер',
      minWidth: 160,
      cellRenderer: ({ data }: ICellRendererParams<BrokerJournalRow>) => (
        <TextCell value={data?.brokerName} />
      ),
    },
    {
      field: 'itemsCount',
      headerName: 'Партномеров',
      minWidth: 120,
      filter: false,
      cellClass: 'text-right',
      headerClass: 'ag-right-aligned-header',
    },
    {
      field: 'answeredCount',
      headerName: 'С ответом',
      minWidth: 110,
      filter: false,
      cellClass: 'text-right',
      headerClass: 'ag-right-aligned-header',
    },
    {
      field: 'createdLabel',
      headerName: 'Создан',
      minWidth: 110,
      filterParams: { dateMode: true },
      comparator: byTime('createdAt'),
    },
    {
      field: 'answeredLabel',
      headerName: 'Ответ получен',
      minWidth: 130,
      filterParams: { dateMode: true },
      comparator: byTime('answeredAt'),
      cellRenderer: ({ data }: ICellRendererParams<BrokerJournalRow>) =>
        data?.answeredLabel ? data.answeredLabel : dash,
    },
    {
      field: 'logistName',
      headerName: 'Логист',
      minWidth: 140,
      cellClass: 'text-gray-600',
      cellRenderer: ({ data }: ICellRendererParams<BrokerJournalRow>) => (
        <TextCell value={data?.logistName} />
      ),
    },
    {
      field: 'note',
      headerName: 'Примечание',
      flex: 1,
      minWidth: 200,
      cellRenderer: ({ data }: ICellRendererParams<BrokerJournalRow>) => (
        <TextCell value={data?.note} />
      ),
    },
  ]
}

// ─── Партномера запроса ─────────────────────────────────────────────────────

export type BrokerItemRow = {
  id: string
  partNumber: string
  brand: string
  description: string
  datasheetUrl: string | null
  /** Код из ответа брокера, иначе — отправленный брокеру. */
  tnvedCode: string
  tnvedFromAnswer: boolean
  positionsCount: number
  status: BrokerRequestItemStatus
  statusLabel: string
  nonTariffNote: string
  customsFee: number | null
  feeLabel: string
  dutyRate: number | null
  dutyLabel: string
  featuresNote: string
}

export function toItemRow(it: BrokerRequestDetailDto['items'][number]): BrokerItemRow {
  const a = it.answer
  return {
    id: it.id,
    partNumber: it.partNumber,
    brand: it.brand ?? '',
    description: it.description ?? a?.description ?? '',
    datasheetUrl: it.datasheetUrl,
    tnvedCode: a?.tnvedCode ?? it.tnvedCode ?? '',
    tnvedFromAnswer: !!a?.tnvedCode,
    positionsCount: it.positionsCount,
    status: it.status,
    statusLabel: BROKER_REQUEST_ITEM_STATUS_LABELS_RU[it.status],
    nonTariffNote: a?.nonTariffNote ?? '',
    customsFee: a?.customsFee ?? null,
    feeLabel: formatCustomsFee(a?.customsFee ?? null),
    dutyRate: a?.dutyRate ?? null,
    dutyLabel: a?.dutyRate == null ? '' : formatDutyRate(a.dutyRate),
    featuresNote: a?.featuresNote ?? '',
  }
}

const wrap = { wrapText: true, autoHeight: true, cellClass: 'leading-snug py-1' } as const

export function buildItemColDefs(): ColDef<BrokerItemRow>[] {
  return [
    { field: 'partNumber', headerName: 'Артикул', minWidth: 170, cellClass: 'font-medium' },
    {
      field: 'brand',
      headerName: 'Производитель',
      minWidth: 130,
      cellRenderer: ({ data }: ICellRendererParams<BrokerItemRow>) => (
        <TextCell value={data?.brand} />
      ),
    },
    {
      field: 'description',
      headerName: 'Описание',
      minWidth: 170,
      ...wrap,
      cellRenderer: ({ data }: ICellRendererParams<BrokerItemRow>) => (
        <TextCell value={data?.description} />
      ),
    },
    {
      field: 'datasheetUrl',
      headerName: 'Даташит',
      minWidth: 100,
      filter: false,
      cellRenderer: ({ data }: ICellRendererParams<BrokerItemRow>) => (
        <DatasheetLink url={data?.datasheetUrl ?? null} />
      ),
    },
    {
      field: 'tnvedCode',
      headerName: 'ТН ВЭД',
      minWidth: 120,
      cellRenderer: ({ data }: ICellRendererParams<BrokerItemRow>) => {
        if (!data?.tnvedCode) return dash
        return data.tnvedFromAnswer ? (
          <span className="font-mono text-xs">{data.tnvedCode}</span>
        ) : (
          <span className="font-mono text-xs text-gray-400" title="Код, отправленный брокеру">
            {data.tnvedCode}
          </span>
        )
      },
    },
    {
      field: 'positionsCount',
      headerName: 'Позиций',
      minWidth: 95,
      filter: false,
      cellClass: 'text-right',
      headerClass: 'ag-right-aligned-header',
    },
    {
      field: 'statusLabel',
      headerName: 'Статус',
      minWidth: 130,
      cellRenderer: ({ data }: ICellRendererParams<BrokerItemRow>) =>
        data ? <BrokerItemStatusBadge status={data.status} /> : null,
    },
    {
      field: 'nonTariffNote',
      headerName: 'Нетарифка',
      minWidth: 220,
      ...wrap,
      cellRenderer: ({ data }: ICellRendererParams<BrokerItemRow>) => (
        <TextCell value={data?.nonTariffNote} />
      ),
    },
    {
      field: 'feeLabel',
      headerName: 'Сбор',
      minWidth: 150,
      comparator: (_a, _b, nA, nB) => (nA.data?.customsFee ?? -1) - (nB.data?.customsFee ?? -1),
      cellRenderer: ({ data }: ICellRendererParams<BrokerItemRow>) => {
        if (!data?.feeLabel) return dash
        const raised = (data.customsFee ?? 0) > 0
        return <span className={raised ? 'text-red-600 font-medium' : ''}>{data.feeLabel}</span>
      },
    },
    {
      field: 'dutyLabel',
      headerName: 'Пошлина',
      minWidth: 100,
      comparator: (_a, _b, nA, nB) => (nA.data?.dutyRate ?? -1) - (nB.data?.dutyRate ?? -1),
      cellRenderer: ({ data }: ICellRendererParams<BrokerItemRow>) => data?.dutyLabel || dash,
    },
    {
      field: 'featuresNote',
      headerName: 'Особенности',
      minWidth: 220,
      flex: 1,
      ...wrap,
      cellRenderer: ({ data }: ICellRendererParams<BrokerItemRow>) => (
        <TextCell value={data?.featuresNote} />
      ),
    },
  ]
}

// ─── Позиции заявок ─────────────────────────────────────────────────────────

export type BrokerPositionRow = {
  id: string
  requestNumber: string
  managerName: string
  partNumber: string
  quantity: number
  status: CustomsCheckStatus
  statusLabel: string
}

export function toPositionRow(p: BrokerRequestDetailDto['positions'][number]): BrokerPositionRow {
  return {
    id: p.itemId,
    requestNumber: p.requestNumber ?? '',
    managerName: p.managerName ?? '',
    partNumber: p.partNumber,
    quantity: p.quantity,
    status: p.status,
    statusLabel: CUSTOMS_STATUS_LABELS_RU[p.status],
  }
}

export function buildPositionColDefs(): ColDef<BrokerPositionRow>[] {
  return [
    {
      field: 'requestNumber',
      headerName: 'Заявка',
      minWidth: 140,
      cellRenderer: ({ data }: ICellRendererParams<BrokerPositionRow>) =>
        data?.requestNumber || dash,
    },
    {
      field: 'managerName',
      headerName: 'Менеджер',
      minWidth: 150,
      cellClass: 'text-gray-600',
      cellRenderer: ({ data }: ICellRendererParams<BrokerPositionRow>) => (
        <TextCell value={data?.managerName} />
      ),
    },
    { field: 'partNumber', headerName: 'Партномер', minWidth: 170, cellClass: 'font-medium' },
    {
      field: 'quantity',
      headerName: 'Кол-во',
      minWidth: 90,
      filter: false,
      cellClass: 'text-right',
      headerClass: 'ag-right-aligned-header',
      valueFormatter: (p) => (p.value == null ? '' : Number(p.value).toLocaleString('ru-RU')),
    },
    {
      field: 'statusLabel',
      headerName: 'Статус нетарифки',
      minWidth: 170,
      flex: 1,
      cellRenderer: ({ data }: ICellRendererParams<BrokerPositionRow>) =>
        data ? <CustomsStatusBadge status={data.status} /> : null,
    },
  ]
}
